// Copyright (c) 2021, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { withRouter } from 'react-router-dom'
import moment from 'moment'

import {
  Spinner,
  SvgIcon,
  Table,
  PageNavigationButtons,
  CenteredContainerDisplay,
  SpanWithData,
  Warning,
  NavLink
} from '../'

import {
  PageNavigationControl,
  showAsCurrency,
  getPageCursor,
  saveCursor,
  decodeBase64
} from '../../utils'


class Withdrawals extends React.Component {
  static propTypes = {
    identityId: PropTypes.string,
    productName: PropTypes.string,
    employerId: PropTypes.string,
    divisionId: PropTypes.string,
    status: PropTypes.string,
    search: PropTypes.string,
    initiateOnStartDate: PropTypes.string,
    initiateOnEndDate: PropTypes.string,
    orderBy: PropTypes.string,
    changeSort: PropTypes.func,
    defaultResultsPerPage: PropTypes.number
  }

  static defaultProps = {
    defaultResultsPerPage: 15,
    className: ''
  }

  state = {
    filterChanged: false,
    pageNav: new PageNavigationControl(),
    resultsPerPage: this.props.defaultResultsPerPage
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if ((this.props.employerId !== nextProps.employerId ||
      this.props.divisionId !== nextProps.divisionId ||
      this.props.employerInstrument !== nextProps.employerInstrument ||
      this.props.status !== nextProps.status ||
      this.props.search !== nextProps.search ||
      this.props.orderBy !== nextProps.orderBy) &&
      !sessionStorage.getItem('withdrawalListPageState')
    ) {
      let previousFilters = JSON.parse(sessionStorage.getItem('withdrawalListFilters'))
      if (previousFilters && (
        previousFilters.employerId === nextProps.employerId,
        previousFilters.divisionId === nextProps.divisionId,
        previousFilters.status === nextProps.status,
        previousFilters.search === nextProps.search ||
        previousFilters.orderBy === nextProps.orderBy
      )) {
        sessionStorage.removeItem('withdrawalListFilters')
      } else {
        this.state.pageNav.reset()
        this.setState({
          filterChanged: true
        }, () => this.props.refetch())
        sessionStorage.removeItem('withdrawalListFilters')
      }
    } else {
      if (!nextProps.loading) {
        if (this.state.filterChanged) {
          this.setState({
            filterChanged: false
          })
          this.state.pageNav.setPageStates(null, nextProps.withdrawals)
        }
        else if (nextProps.withdrawals) {
          let withdrawalListPageState = JSON.parse(sessionStorage.getItem('withdrawalListPageState'))
          if (withdrawalListPageState) {
            this.state.pageNav.setPageStates(null, null, withdrawalListPageState)
            sessionStorage.removeItem('withdrawalListPageState')
            if (withdrawalListPageState.resultsPerPage !== this.state.resultsPerPage) {
              this.setState({ resultsPerPage: withdrawalListPageState.resultsPerPage })
            }
            this.props.refetch({
              cursor: withdrawalListPageState.cursor,
              resultsPerPage: withdrawalListPageState.resultsPerPage
            })
          } else if (!Object.is(this.props.withdrawals, nextProps.withdrawals)) {
            this.state.pageNav.setPageStates(this.props.withdrawals, nextProps.withdrawals)
          }
        }
      }
    }
  }

  savePaginationAndFilterState = (startCursor) => {
    sessionStorage.setItem('withdrawalListPageState', JSON.stringify({
      firstPage: this.state.pageNav.firstPage,
      lastPage: this.state.pageNav.lastPage,
      currentPage: this.state.pageNav.currentPage,
      pages: this.state.pageNav.pages,
      cursor: saveCursor(startCursor),
      resultsPerPage: this.state.resultsPerPage
    }))
    sessionStorage.setItem('withdrawalListFilters', JSON.stringify({
      employerId: this.props.employerId,
      divisionId: this.props.divisionId,
      search: this.props.search,
      orderBy: this.props.orderBy
    }))
  }

  saveStateAndGoToDetailPage = (withdrawal, startCursor) => {
    this.savePaginationAndFilterState(startCursor)
    this.props.history.push(
      `/os/identity/${withdrawal.client.identity.id}/products/withdrawals/${withdrawal.id}`
    )
  }

  changeResultsPerPage = (e) => {
    let resultsPerPage = parseInt(e.target.value, 10)
    this.setState({ resultsPerPage }, () => this.props.refetch({ resultsPerPage, cursor: null }))
  }

  render() {
    if (this.props.loading) {
      return (
        <CenteredContainerDisplay
          content={
            <Spinner className='slds-is-relative' />  // TODO: loading state stencil instead
          }
          yAdjustPercentage={-15}
        />
      )
    }

    if (this.props.error) {
      return (
        <Warning>
          Error fetching withdrawal data
        </Warning>
      )
    }

    if (this.props.withdrawals.edges.length === 0) {
      return (
        <div className='slds-m-around_large'>
          <SvgIcon
            variant='utility'
            symbol='info'
            colour='light'
            size='small'
            className='slds-m-right_small'
          />
          No withdrawals found
        </div>
      )
    }

    let pageNav = this.state.pageNav
    let tableColumns = this.props.condensedView  // NOTE: at the moment no distinction
      ? ['Client', 'Withdraw from', 'Withdraw to', 'Amount', 'Amount type', 'Status', 'Initiate on', 'Batch']
      : ['Client', 'Withdraw from', 'Withdraw to', 'Amount', 'Amount type', 'Status', 'Initiate on', 'Batch']

    if (this.props.identityId) {
      tableColumns.shift()
    }

    if (this.props.actionColumnRender) {
      tableColumns.push('Action')
    }

    let withdrawals = this.props.withdrawals
    let startCursor = withdrawals.pageInfo.startCursor
    let totalCount = withdrawals.totalCount

    return (
      <div className={this.props.className}>
        <Table
          columns={tableColumns}
          rows={withdrawals.edges.map((withdrawal, index) => (
            <WithdrawalDetailsRow
              withdrawal={withdrawal.node}
              onClick={withdrawal => this.saveStateAndGoToDetailPage(withdrawal, withdrawals.pageInfo.startCursor)}
              actionColumnRender={this.props.actionColumnRender}
              condensedView={this.props.condensedView}
              identityId={this.props.identityId}
              key={`withdrawalDetailsRow_${index}`}
            />
          ))}
          objectRows={true}
          orderBy={this.props.orderBy}
          changeSort={this.props.changeSort}
          querySortableColumns={{
            'Initiate on': {
              orderByName: 'initiate_on'
            }
          }}
        />
        <PageNavigationButtons
          onNextClick={() => pageNav.nextPage(this.props.fetchNext, this.state.resultsPerPage)}
          onPreviousClick={() => pageNav.previousPage(this.props.fetchPrevious, this.state.resultsPerPage)}
          previousDisabled={pageNav.firstPage}
          nextDisabled={pageNav.lastPage}
          startCursor={startCursor}
          totalCount={totalCount}
          resultsPerPage={this.state.resultsPerPage}
          resultsText='records'
          onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.state.resultsPerPage) })}
          onResultsPerPageChange={this.changeResultsPerPage}
          className='slds-p-around_medium'
        />
      </div>
    )
  }
}

const WithdrawalDetailsRow = ({ withdrawal, actionColumnRender, condensedView, onClick, identityId }) => (
  <tr className='clickable' onClick={() => onClick(withdrawal)}>
    {!identityId &&
      <td data-label='Client'>
        {`${withdrawal.client.identity.fullName}`}
      </td>
    }
    <td data-label='Withdraw from'>
      {`${withdrawal.account.description}`}
    </td>
    <td data-label='Withdraw to'>
      {withdrawal.targetProduct
        ? withdrawal.targetProduct.name
        : withdrawal.targetBankaccount
          ? <span>
              {withdrawal.targetBankaccount.bankaccount.accountNumber} (
              <SpanWithData
                forField='BankAccountBankName'
                searchKey={withdrawal.targetBankaccount.bankaccount.bankName}
              />
              )
            </span>
          : '-'
      }
    </td>
    <td data-label='Amount requested'>
      {withdrawal.amountType === 'FIXED'
        ? showAsCurrency('R', withdrawal.amountRequested)
        : `${parseInt(withdrawal.amountRequested, 10)}%`
      }
    </td>
    <td data-label='Amount type'>
      <SpanWithData forField='WithdrawalAmountType' searchKey={withdrawal.amountType} />
    </td>
    <td data-label='Status'>
      <SpanWithData forField='WithdrawalStatus' searchKey={withdrawal.status} />
    </td>
    <td data-label='Initiate on'>
      {moment(withdrawal.initiateOn).format('DD MMM YYYY')}
    </td>
    <td data-label='Batch'>
      {withdrawal.withdrawalbatch
        ? <NavLink
            to={`/os/products/withdrawalbatches/${withdrawal.withdrawalbatch.id}`}
            onClick={e => e.stopPropagation()}>
            {moment(withdrawal.withdrawalbatch.initiateOn).format('DD MMM YYYY')}
          </NavLink>
        : '-'
      }
    </td>
  </tr>
)

const getWithdrawals = gql`
  query($identity: UUID, $withdrawalBatch: UUID, $employer: UUID, $division: UUID, $product: String, $status: String!, $search: String!, $initiateOnStartDate: Date, $initiateOnEndDate: Date, $orderBy: String, $resultsPerPage: Int!, $cursor: String) {
    withdrawals(client_Identity_Id: $identity, withdrawalbatch_Id: $withdrawalBatch, client_Division_Employer_Id: $employer, client_Division_Id: $division, product: $product, status: $status, initiateOn_Gte: $initiateOnStartDate, initiateOn_Lte: $initiateOnEndDate, search: $search, orderBy: $orderBy, first: $resultsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          id
          amountRequested
          amountType
          initiateOn
          status
          client {
            id
            identity {
              id
              fullName
            }
          }
          account {
            id
            description
          }
          targetBankaccount {
            id
            bankaccount {
              id
              bankName
              accountNumber
            }
          }
          targetProduct {
            id
            name
          }
          withdrawalbatch {
            id
            initiateOn
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const getPreviousWithdrawals = gql`
  query($identity: UUID, $withdrawalBatch: UUID, $employer: UUID, $division: UUID, $product: String, $status: String!, $search: String!, $initiateOnStartDate: Date, $initiateOnEndDate: Date, $orderBy: String, $resultsPerPage: Int!, $cursor: String) {
    withdrawals(client_Identity_Id: $identity, withdrawalbatch_Id: $withdrawalBatch, client_Division_Employer_Id: $employer, client_Division_Id: $division, product: $product, status: $status, search: $search, initiateOn_Gte: $initiateOnStartDate, initiateOn_Lte: $initiateOnEndDate, orderBy: $orderBy, last: $resultsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          id
          amountType
          status
          client {
            id
            identity {
              id
              fullName
            }
          }
          account {
            id
            description
          }
          targetBankaccount {
            id
            bankaccount {
              id
              bankName
              accountNumber
            }
          }
          targetProduct {
            id
            name
          }
          withdrawalbatch {
            id
            initiateOn
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const withData =
  graphql(getWithdrawals, {
    options: ({ identityId, employerId, divisionId, productName, status, search, initiateOnStartDate, initiateOnEndDate, withdrawalBatchId, orderBy, defaultResultsPerPage = 15, cursor }) => ({
      variables: {
        identity: identityId ? decodeBase64(identityId) : undefined,
        employer: employerId ? decodeBase64(employerId) : undefined,
        division: divisionId ? decodeBase64(divisionId) : undefined,
        product: productName,
        status,
        search,
        initiateOnStartDate,
        initiateOnEndDate,
        withdrawalBatch: withdrawalBatchId ? decodeBase64(withdrawalBatchId) : undefined,
        orderBy,
        resultsPerPage: defaultResultsPerPage,
        cursor
      },
      fetchPolicy: 'network-only'
    }),
    props: ({ ownProps: { identityId, employerId, divisionId, productName, status, search, initiateOnStartDate, initiateOnEndDate, withdrawalBatchId, orderBy }, data: { loading, error, refetch, withdrawals, fetchMore } }) => ({
      loading,
      error,
      refetch,
      withdrawals,
      fetchNext: (resultsPerPage) => {
        return fetchMore({
          query: getWithdrawals,
          variables: {
            identity: identityId ? decodeBase64(identityId) : undefined,
            employer: employerId ? decodeBase64(employerId) : undefined,
            division: divisionId ? decodeBase64(divisionId) : undefined,
            product: productName,
            status,
            search,
            initiateOnStartDate,
            initiateOnEndDate,
            withdrawalBatch: withdrawalBatchId ? decodeBase64(withdrawalBatchId) : undefined,
            orderBy,
            resultsPerPage,
            cursor: withdrawals.pageInfo.endCursor
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.withdrawals.edges
            const pageInfo = fetchMoreResult.withdrawals.pageInfo
            const totalCount = fetchMoreResult.withdrawals.totalCount

            return {
              withdrawals: {
                __typename: previousResult.withdrawals.__typename,
                totalCount,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      },
      fetchPrevious: (resultsPerPage) => {
        return fetchMore({
          query: getPreviousWithdrawals,
          variables: {
            identity: identityId ? decodeBase64(identityId) : undefined,
            employer: employerId ? decodeBase64(employerId) : undefined,
            division: divisionId ? decodeBase64(divisionId) : undefined,
            product: productName,
            status,
            search,
            initiateOnStartDate,
            initiateOnEndDate,
            withdrawalBatch: withdrawalBatchId ? decodeBase64(withdrawalBatchId) : undefined,
            orderBy,
            resultsPerPage,
            cursor: withdrawals.pageInfo.startCursor
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.withdrawals.edges
            const pageInfo = fetchMoreResult.withdrawals.pageInfo
            const totalCount = fetchMoreResult.withdrawals.totalCount

            return {
              withdrawals: {
                __typename: previousResult.withdrawals.__typename,
                totalCount,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      }
    })
  })

export default compose(
  withData,
  withRouter
)(Withdrawals)



// WEBPACK FOOTER //
// ./src/components/withdrawals/index.js